import { capitalizeFirstLetter } from '@/utils/helpers/StringOperations'

export class DataLoader {
  constructor ($store, modelActionPayloads, setLoadingStatus, pageSize = 20) {
    this.$store = $store || {}
    this.pageSize = pageSize
    this.modelActionPayloads = modelActionPayloads || []
    this.completedRequests = 0
    this.totalRequests = 0
    this.failedBatches = []
    this.getAddressById = $store.getters && typeof $store.getters.getAddressById === 'function' ? $store.getters.getAddressById : {}
    this.percentComplete = () => (this.completedRequests / this.totalRequests) * 100
    this.dataLoaded = false
    this.setLoadingStatus = setLoadingStatus || false
  }

  /**
   * Loads all data
   */
  async loadData (fromBeforeRoute = false) {
    let promises = [...this.loadAllModels()]
    this.totalRequests = promises.length
    if (this.setLoadingStatus) {
      this.$store.dispatch('setInitDataLoadStatus', false)
      this.$store.dispatch('setInitDataLoadingStatus', true)
    }
    try {
      let response = await Promise.all(promises)
      let requests = this.flattenRequestArrays(response)
      this.totalRequests = this.totalRequests + requests.length
      return await this.handleSubsequentRequests(requests)
    } catch (e) {
      if (this.setLoadingStatus) {
        this.$store.dispatch('setInitDataLoadStatus', false)
      }
      console.log(e)
      return e
    }
  }

  /**
   * Loads all data
   */
  async loadDataBeforeRoute (fromBeforeRoute = false) {
    let promises = [...this.loadAllModels()]
    this.totalRequests = promises.length
    try {
      let response = await Promise.all(promises)
      let requests = this.flattenRequestArrays(response)
      this.totalRequests = this.totalRequests + requests.length
      return await this.handleBeforeRouteSubsequentRequests(requests)
    } catch (e) {
      console.log(e)
      return e
    }
  }
  /**
   * Handles subsequent requests
   * @param requests
   * @param args
   */
  async handleBeforeRouteSubsequentRequests (requests, args = null) {
    if (!args) {
      args = { attempt: 0, maxToSend: 10, maxAttempts: 1 }
    }
    if (requests.length) {
      await this.sendRequestsInBatches(requests, args, true)
    } else {
      args.attempt++
      if (this.failedBatches.length) {  // Retry once if a batch of requests contains failed requests
        return this.handleBeforeRouteSubsequentRequests(this.failedBatches, args)
      }
    }
  }
  /**
   * Loads all models that need to be pre-loaded
   */
  loadAllModels () {
    return this.modelActionPayloads.map(typePayload => {
      let payload = { ...typePayload, page: 1, query: { page_size: this.pageSize } }
      payload.skipCacheCheck = true
      return this.$store.dispatch('fetchWithQuery', payload)
        .then(r => {
          if (r) this.completedRequests++
          const { count } = this.$store.getters[`get${capitalizeFirstLetter(typePayload.moduleName)}Metadata`]
          return this.generatePageRequests(count, payload)
        })
        .catch(e => console.log(e))
    })
  }

  /**
   * Sends preloading requests in batches of 10
   */
  async sendRequestsInBatches (requests = [], { attempt = 0, maxToSend = 10, maxAttempts = 1 } = {}, isBeforeRoute = false) {
    if (attempt <= maxAttempts) {
      try {
        const batch = requests.splice(0, maxToSend)
        const responses = await Promise.all(batch)
        responses.forEach((r, i) => {
          if (!r || r && r.hasOwnProperty('ok') && !r.ok) {
            this.failedBatches = [...this.failedBatches, ...batch]
          }
        })
        if (isBeforeRoute) this.handleBeforeRouteSubsequentRequests(requests, { attempt, maxToSend, maxAttempts })
        else this.handleSubsequentRequests(requests, { attempt, maxToSend, maxAttempts })
        return responses
      } catch (e) {
        console.log(e)
        return e
      }
    }
  }

  /**
   * Handles subsequent requests
   * @param requests
   * @param args
   */
  async handleSubsequentRequests (requests, args = null) {
    if (!args) {
      args = { attempt: 0, maxToSend: 10, maxAttempts: 1 }
    }
    if (requests.length) {
      await this.sendRequestsInBatches(requests, args)
    } else {
      args.attempt++
      if (this.failedBatches.length) {  // Retry once if a batch of requests contains failed requests
        return this.handleSubsequentRequests(this.failedBatches, args)
      }
      // this.fetchBankDependencies()
      if (this.setLoadingStatus) {
        this.$store.dispatch('setInitDataLoadingStatus', false)
        this.$store.dispatch('setInitDataLoadStatus', true)
      }
    }
  }

  /**
   * Takes an array containing arrays of requests and flattens it
   * @param requestArrays
   */
  flattenRequestArrays (requestArrays) {
    let allRequests = []
    requestArrays.forEach(v => {
      if (v.length) {
        allRequests = [...allRequests, ...v]
      }
    })
    return allRequests
  }

  /**
   * Generates page requests
   * @param count   Total count
   * @param payload
   */
  generatePageRequests (count, payload) {
    let requests = []
    if (count && count > this.pageSize) {
      const requestCount = Math.ceil(count / this.pageSize)
      const query = payload.query || {}
      for (let i = 2; i <= requestCount; i++) {
        query.page = i
        requests.push(this.$store.dispatch('fetchWithQuery', payload)
          .then(r => {
            if (r) {
              this.completedRequests++
              return r
            }
          })
        )
      }
    }
    return requests
  }

  async fetchBankDependencies () {
    const allBanks = this.$store.getters.getAllBanks
    let entities = { banks: {} }
    allBanks.forEach(v => {
      entities.banks[v.id] = v
    })
    try {
      const promises = await this.$store.dispatch('findDependencies', entities)
      await Promise.all(promises)
      const mailingAddresses = allBanks.map(v => v.address_mailing)
      const physicalAddresses = allBanks.map(v => v.address_physical)
      const allAddresses = [...new Set([].concat(...[...mailingAddresses, ...physicalAddresses]))]
      let addressEntities = { addresses: {} }
      allAddresses.forEach(v => {
        const address = this.getAddressById(v)
        if (address) {
          addressEntities.addresses[v] = address
        }
      })
      const promises2 = await this.$store.dispatch('findDependencies', addressEntities)
      return await Promise.all(promises2)
    } catch (e) {
      console.log(e)
      return e
    }
  }
}



// WEBPACK FOOTER //
// ./src/utils/helpers/DataLoader/DataLoader.js